import React, { useEffect, useMemo, useState, Fragment } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { motion, AnimatePresence } from 'framer-motion'
import { initialFormData, IntakeFormData } from '../types/intake'
import { ProgressBar } from '../components/intake/shared/ProgressBar'
import { QuestionStep } from '../components/intake/shared/QuestionStep'
import { TextInput } from '../components/intake/shared/TextInput'
import { RadioGroup } from '../components/intake/shared/RadioGroup'
import { ScaleQuestion } from '../components/intake/shared/ScaleQuestion'
import { MultiSelect } from '../components/intake/shared/MultiSelect'
import { WelcomeStep } from '../components/intake/WelcomeStep'
import { CompletionStep } from '../components/intake/CompletionStep'
import { SuccessStep } from '../components/intake/SuccessStep'
import { buildIntakePayload } from '../lib/intake/buildIntakePayload'
import { useI18n } from "../i18n/I18nProvider";
type QuestionConfig = {
  section: string
  render: (
    data: IntakeFormData,
    updateData: (fields: Partial<IntakeFormData>) => void,
  ) => JSX.Element
  isValid: (data: IntakeFormData) => boolean
  helperText?: string
  shouldShow?: (data: IntakeFormData) => boolean
}
export function IntakeFlow() {
  const [currentIndex, setCurrentIndex] = useState(0)
  const [formData, setFormData] = useState<IntakeFormData>(initialFormData)
  const [direction, setDirection] = useState(1)
  // Language switcher UI state (no behavior beyond selection + UI)
  const [lang, setLang] = useState<string>('en')
  const [langOpen, setLangOpen] = useState(false)
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()
  const { locale, setLocale, t } = useI18n()
  const languageOptions = [
    { label: t('lang.en'), value: 'en' },
    { label: t('lang.fr'), value: 'fr' },
    { label: t('lang.ar'), value: 'ar' },
    { label: t('lang.ur'), value: 'ur' },
  ] as const

  const LS_EMAIL = 'niyyah_email'
  const LS_PLAN = 'niyyah_plan'
  const isUpgradeFlow = () => {
    const step = (searchParams.get("step") || "").toLowerCase()
    const plan = (searchParams.get("plan") || "").toLowerCase()
    return step === "payment" || step === "checkout" || step === "upgrade" || plan === "active" || plan === "basic"
  }

  const updateData = (fields: Partial<IntakeFormData>) => {
    setFormData((prev) => ({
      ...prev,
      ...fields,
    }))
  }
  const isGuardianIntake = (data: IntakeFormData) => data.intakeRole === 'guardian'
  useEffect(() => {
    const email = (formData.email || '').trim().toLowerCase()
    if (!email) return
    try {
      localStorage.setItem(LS_EMAIL, email)
    } catch {}
  }, [formData.email])

  const [isLocaleTransitioning, setIsLocaleTransitioning] = useState(false)
  const [nextLocale, setNextLocale] = useState<string | null>(null)

  const handleLocaleSelect = (code: string) => {
    // if same locale, just close the picker
    if (code === locale) {
      setLangOpen(false)
      return
    }

    // Desktop: close immediately (feels best)
    // Mobile: KEEP overlay open to prevent seeing the EN underlay during fade
    if (!isMobile) setLangOpen(false)

    setNextLocale(code)
    setIsLocaleTransitioning(true)
  }

  const isMobile = useMemo(() => {
    if (typeof window === 'undefined') return false
    return window.matchMedia('(max-width: 639px)').matches // Tailwind sm breakpoint is 640
  }, [])

  // Debounce email for member-status lookup (prevents spamming)
  const [debouncedEmail, setDebouncedEmail] = useState('')

  useEffect(() => {
    const t = setTimeout(() => {
      setDebouncedEmail((formData.email || '').trim().toLowerCase())
    }, 400)
    return () => clearTimeout(t)
  }, [formData.email])

  // Define all questions
  const questions: QuestionConfig[] = [
    // -------------------------
    // GETTING STARTED
    // -------------------------
    {
      section: t('intake.sections.gettingStarted'),
      render: (data, update) => {
        const options = [
          { label: t('intake.gettingStarted.forMe'), value: 'candidate' as const },
          {
            label: t('intake.gettingStarted.forSomeoneElse'),
            value: 'guardian' as const,
          },
        ]

        const selectedLabel =
          options.find((o) => o.value === data.intakeRole)?.label ?? ''

        return (
          <div className="space-y-12">
            <h3 className="text-3xl sm:text-4xl font-normal text-[#2D2A35] text-center">
              {t('intake.gettingStarted.q_forWho')}
            </h3>

            <RadioGroup
              options={options.map((o) => o.label)}
              value={selectedLabel}
              onChange={(label) => {
                const selected = options.find((o) => o.label === label)?.value ?? ''
                update({
                  intakeRole: selected,
                  guardianRelationship: '',
                  candidateApprovesEveryIntro: false,
                })
              }}
            />
          </div>
        )
      },
      isValid: (data) => data.intakeRole === 'candidate' || data.intakeRole === 'guardian',
    },

    {
      section: t('intake.sections.gettingStarted'),
      shouldShow: (data) => data.intakeRole === 'guardian',
      render: (data, update) => {
        const options = [
          { label: t('intake.roles.parent'), value: 'parent' as const },
          { label: t('intake.roles.wali'), value: 'wali' as const },
          { label: t('intake.roles.sibling'), value: 'sibling' as const },
          { label: t('intake.roles.friend'), value: 'friend' as const },
          { label: t('intake.roles.other'), value: 'other' as const },
        ]

        const selectedLabel =
          options.find((o) => o.value === data.guardianRelationship)?.label ?? ''

        return (
          <div className="space-y-12">
            <h3 className="text-3xl sm:text-4xl font-normal text-[#2D2A35] text-center">
              {t('intake.gettingStarted.q_role')}
            </h3>

            <RadioGroup
              options={options.map((o) => o.label)}
              value={selectedLabel}
              onChange={(label) => {
                const selected =
                  options.find((o) => o.label === label)?.value ?? ''
                update({
                  guardianRelationship: selected,
                  candidateApprovesEveryIntro: true,
                })
              }}
            />

            <p className="text-sm text-[#6B6676] text-center">
              {t('intake.gettingStarted.guardianNote')}
            </p>
          </div>
        )
      },
      isValid: (data) => data.intakeRole !== 'guardian' || !!data.guardianRelationship,
    },

    // -------------------------
    // WHO YOU’RE HELPING (guardian)
    // -------------------------
    {
      section: t('intake.sections.whoYoureHelping'),
      shouldShow: (data) => data.intakeRole === 'guardian',
      render: (data, update) => (
        <div className="max-w-xl mx-auto">
          <TextInput
            label={t('intake.whoHelping.firstName')}
            value={data.rc_firstName}
            onChange={(e) => update({ rc_firstName: e.target.value })}
            placeholder={t('intake.placeholders.firstName')}
            autoFocus
          />
        </div>
      ),
      isValid: (data) => data.intakeRole !== 'guardian' || !!data.rc_firstName,
    },

    {
      section: t('intake.sections.whoYoureHelping'),
      shouldShow: (data) => data.intakeRole === 'guardian',
      render: (data, update) => (
        <div className="flex justify-center">
          <TextInput
            label={t('intake.whoHelping.age')}
            type="number"
            value={data.rc_age}
            onChange={(e) => update({ rc_age: e.target.value })}
            placeholder={t('intake.placeholders.age')}
            className="max-w-[200px] text-center"
          />
        </div>
      ),
      isValid: (data) =>
        data.intakeRole !== 'guardian' || (!!data.rc_age && parseInt(data.rc_age) >= 18),
    },

    {
      section: t('intake.sections.whoYoureHelping'),
      shouldShow: (data) => data.intakeRole === 'guardian',
      render: (data, update) => {
        const options = [
          { label: t('common.male'), value: 'male' as const },
          { label: t('common.female'), value: 'female' as const },
        ]
        const selectedLabel =
          options.find((o) => o.value === data.rc_gender)?.label ?? ''

        return (
          <div className="space-y-12">
            <h3 className="text-3xl sm:text-4xl font-normal text-[#2D2A35] text-center">
              {t('intake.whoHelping.gender')}
            </h3>
            <RadioGroup
              options={options.map((o) => o.label)}
              value={selectedLabel}
              onChange={(label) => {
                const selected = options.find((o) => o.label === label)?.value
                update({ rc_gender: (selected ?? '') as any })
              }}
            />
          </div>
        )
      },
      isValid: (data) => data.intakeRole !== 'guardian' || !!data.rc_gender,
    },

    {
      section: t('intake.sections.whoYoureHelping'),
      shouldShow: (data) => data.intakeRole === 'guardian',
      render: (data, update) => (
        <div className="max-w-xl mx-auto">
          <TextInput
            label={t('intake.whoHelping.location')}
            value={data.rc_city}
            onChange={(e) => update({ rc_city: e.target.value })}
            placeholder={t('intake.placeholders.cityShort')}
            autoFocus
          />
        </div>
      ),
      isValid: (data) => data.intakeRole !== 'guardian' || !!data.rc_city,
    },

    // -------------------------
    // THE BASICS
    // -------------------------
    {
      section: t('intake.sections.basics'),
      render: (data, update) => (
        <div className="max-w-xl mx-auto">
          <TextInput
            label={t('intake.basics.firstName')}
            value={data.firstName}
            onChange={(e) => update({ firstName: e.target.value })}
            placeholder={t('intake.placeholders.firstName')}
            autoFocus
          />
        </div>
      ),
      isValid: (data) => !!data.firstName,
    },

    {
      section: t('intake.sections.basics'),
      render: (data, update) => (
        <div className="flex justify-center">
          <TextInput
            label={t('intake.basics.age')}
            type="number"
            value={data.age}
            onChange={(e) => update({ age: e.target.value })}
            placeholder={t('intake.placeholders.age')}
            className="max-w-[200px] text-center"
            error={data.age && parseInt(data.age) < 18 ? t('intake.errors.age18') : undefined}
            autoFocus
          />
        </div>
      ),
      isValid: (data) => !!data.age && parseInt(data.age) >= 18,
    },

    {
      section: t('intake.sections.basics'),
      render: (data, update) => {
        const options = [
          { label: t('common.male'), value: 'male' as const },
          { label: t('common.female'), value: 'female' as const },
        ]
        const selectedLabel =
          options.find((o) => o.value === data.gender)?.label ?? ''

        return (
          <div className="space-y-12">
            <h3 className="text-3xl sm:text-4xl font-normal text-[#2D2A35] text-center">
              {t('intake.basics.gender')}
            </h3>
            <RadioGroup
              options={options.map((o) => o.label)}
              value={selectedLabel}
              onChange={(label) => {
                const selected = options.find((o) => o.label === label)?.value
                update({ gender: (selected ?? '') as any })
              }}
            />
          </div>
        )
      },
      isValid: (data) => !!data.gender,
    },

    {
      section: t('intake.sections.basics'),
      render: (data, update) => (
        <div className="max-w-xl mx-auto">
          <TextInput
            label={t('intake.basics.location')}
            value={data.city}
            onChange={(e) => update({ city: e.target.value })}
            placeholder={t('intake.placeholders.cityLong')}
            autoFocus
          />
        </div>
      ),
      isValid: (data) => !!data.city,
    },

    {
      section: t('intake.sections.basics'),
      render: (data, update) => {
        // IMPORTANT: keep stored values EXACTLY as "Yes" | "Maybe" | "Not right now" for your payload mapping
        const options = [
          { label: t('inPerson.yes'), value: 'Yes' as const },
          { label: t('inPerson.maybe'), value: 'Maybe' as const },
          { label: t('inPerson.notNow'), value: 'Not right now' as const },
        ]
        const selectedLabel =
          options.find((o) => o.value === data.inPerson)?.label ?? ''

        return (
          <div className="space-y-12">
            <h3 className="text-3xl sm:text-4xl font-normal text-[#2D2A35] text-center">
              {t('intake.basics.inPerson')}
            </h3>
            <RadioGroup
              options={options.map((o) => o.label)}
              value={selectedLabel}
              onChange={(label) => {
                const selected = options.find((o) => o.label === label)?.value ?? ''
                update({ inPerson: selected as any })
              }}
            />
          </div>
        )
      },
      isValid: (data) => !!data.inPerson,
    },

    // -------------------------
    // LANGUAGE & COMMUNICATION
    // -------------------------
    {
      section: t('intake.sections.language'),
      render: (data, update) => (
        <div className="space-y-12">
          <MultiSelect
            label={
              isGuardianIntake(data)
                ? t('intake.language.guardianLabel')
                : t('intake.language.spokenLabel')
            }
            options={languageOptions}
            selected={data.languagesSpoken}
            onChange={(selected) => update({ languagesSpoken: selected })}
          />

          <div className="max-w-xl mx-auto pt-8">
            <TextInput
              label={t('intake.language.other')}
              value={data.otherLanguage}
              onChange={(e) => update({ otherLanguage: e.target.value })}
              placeholder={t('intake.placeholders.otherLanguages')}
            />
          </div>
        </div>
      ),
      isValid: (data) => data.languagesSpoken.length > 0 || !!data.otherLanguage,
      helperText: t('intake.language.helper'),
    },

    {
      section: t('intake.sections.language'),
      shouldShow: (data) => data.intakeRole !== 'guardian',
      render: (data, update) => (
        <div className="space-y-12">
          <MultiSelect
            label={t('intake.language.preferredLabel')}
            options={languageOptions}
            selected={data.languagesPreferred}
            onChange={(selected) => update({ languagesPreferred: selected })}
          />

          <div className="max-w-xl mx-auto pt-8">
            <TextInput
              label={t('intake.language.other')}
              value={data.otherLanguagePreferred}
              onChange={(e) => update({ otherLanguagePreferred: e.target.value })}
              placeholder={t('intake.placeholders.otherLanguages')}
            />
          </div>
        </div>
      ),
      isValid: () => true,
    },

    // -------------------------
    // LIFESTYLE & ENERGY
    // -------------------------
    {
      section: t('intake.sections.lifestyle'),
      shouldShow: (data) => data.intakeRole !== 'guardian',
      render: (data, update) => (
        <ScaleQuestion
          question={t('intake.lifestyle.q_activity')}
          minLabel={t('intake.lifestyle.min_restless')}
          maxLabel={t('intake.lifestyle.max_content')}
          value={data.lifestyle_activity}
          onChange={(val) => update({ lifestyle_activity: val })}
        />
      ),
      isValid: (data) => data.lifestyle_activity !== null,
    },
    {
      section: t('intake.sections.lifestyle'),
      shouldShow: (data) => data.intakeRole !== 'guardian',
      render: (data, update) => (
        <ScaleQuestion
          question={t('intake.lifestyle.q_travelAnxious')}
          minLabel={`${t('common.strongly')}|${t('common.disagree')}`}
          maxLabel={`${t('common.strongly')}|${t('common.agree')}`}
          value={data.lifestyle_travel}
          onChange={(val) => update({ lifestyle_travel: val })}
        />
      ),
      isValid: (data) => data.lifestyle_travel !== null,
    },
    {
      section: t('intake.sections.lifestyle'),
      shouldShow: (data) => data.intakeRole !== 'guardian',
      render: (data, update) => (
        <ScaleQuestion
          question={t('intake.lifestyle.q_vacation')}
          minLabel={t('intake.lifestyle.min_relaxing')}
          maxLabel={t('intake.lifestyle.max_fullSchedule')}
          value={data.lifestyle_vacation}
          onChange={(val) => update({ lifestyle_vacation: val })}
        />
      ),
      isValid: (data) => data.lifestyle_vacation !== null,
    },
    {
      section: t('intake.sections.lifestyle'),
      shouldShow: (data) => data.intakeRole !== 'guardian',
      render: (data, update) => (
        <ScaleQuestion
          question={t('intake.lifestyle.q_exercise')}
          minLabel={`${t('common.strongly')}|${t('common.disagree')}`}
          maxLabel={`${t('common.strongly')}|${t('common.agree')}`}
          value={data.lifestyle_exercise}
          onChange={(val) => update({ lifestyle_exercise: val })}
        />
      ),
      isValid: (data) => data.lifestyle_exercise !== null,
    },
    {
      section: t('intake.sections.lifestyle'),
      shouldShow: (data) => data.intakeRole !== 'guardian',
      render: (data, update) => (
        <ScaleQuestion
          question={t('intake.lifestyle.q_diet')}
          minLabel={`${t('common.strongly')}|${t('common.disagree')}`}
          maxLabel={`${t('common.strongly')}|${t('common.agree')}`}
          value={data.lifestyle_diet}
          onChange={(val) => update({ lifestyle_diet: val })}
        />
      ),
      isValid: (data) => data.lifestyle_diet !== null,
    },

    // -------------------------
    // RELATIONSHIP STYLE
    // -------------------------
    {
      section: t('intake.sections.relationship'),
      shouldShow: (data) => data.intakeRole !== 'guardian',
      render: (data, update) => (
        <ScaleQuestion
          question={t('intake.relationship.q_romance')}
          minLabel={`${t('common.strongly')}|${t('common.disagree')}`}
          maxLabel={`${t('common.strongly')}|${t('common.agree')}`}
          value={data.rel_romance}
          onChange={(val) => update({ rel_romance: val })}
        />
      ),
      isValid: (data) => data.rel_romance !== null,
    },
    {
      section: t('intake.sections.relationship'),
      shouldShow: (data) => data.intakeRole !== 'guardian',
      render: (data, update) => (
        <ScaleQuestion
          question={t('intake.relationship.q_conflict')}
          minLabel={`${t('common.strongly')}|${t('common.disagree')}`}
          maxLabel={`${t('common.strongly')}|${t('common.agree')}`}
          value={data.rel_conflict}
          onChange={(val) => update({ rel_conflict: val })}
        />
      ),
      isValid: (data) => data.rel_conflict !== null,
    },
    {
      section: t('intake.sections.relationship'),
      shouldShow: (data) => data.intakeRole !== 'guardian',
      render: (data, update) => (
        <ScaleQuestion
          question={t('intake.relationship.q_processing')}
          minLabel={`${t('common.strongly')}|${t('common.disagree')}`}
          maxLabel={`${t('common.strongly')}|${t('common.agree')}`}
          value={data.rel_processing}
          onChange={(val) => update({ rel_processing: val })}
        />
      ),
      isValid: (data) => data.rel_processing !== null,
    },
    {
      section: t('intake.sections.relationship'),
      shouldShow: (data) => data.intakeRole !== 'guardian',
      render: (data, update) => (
        <ScaleQuestion
          question={t('intake.relationship.q_similarity')}
          minLabel={`${t('common.strongly')}|${t('common.disagree')}`}
          maxLabel={`${t('common.strongly')}|${t('common.agree')}`}
          value={data.rel_similarity}
          onChange={(val) => update({ rel_similarity: val })}
        />
      ),
      isValid: (data) => data.rel_similarity !== null,
    },
    {
      section: t('intake.sections.relationship'),
      shouldShow: (data) => data.intakeRole !== 'guardian',
      render: (data, update) => (
        <ScaleQuestion
          question={t('intake.relationship.q_openness')}
          minLabel={`${t('common.strongly')}|${t('common.disagree')}`}
          maxLabel={`${t('common.strongly')}|${t('common.agree')}`}
          value={data.rel_openness}
          onChange={(val) => update({ rel_openness: val })}
        />
      ),
      isValid: (data) => data.rel_openness !== null,
    },

    // -------------------------
    // MARRIAGE & FAMILY
    // -------------------------
    {
      section: t('intake.sections.family'),
      shouldShow: (data) => data.intakeRole !== 'guardian',
      render: (data, update) => (
        <ScaleQuestion
          question={t('intake.family.q_marriageYoung')}
          minLabel={`${t('common.strongly')}|${t('common.disagree')}`}
          maxLabel={`${t('common.strongly')}|${t('common.agree')}`}
          value={data.fam_marriage_age}
          onChange={(val) => update({ fam_marriage_age: val })}
        />
      ),
      isValid: (data) => data.fam_marriage_age !== null,
    },

    {
      section: t('intake.sections.family'),
      render: (data, update) => {
        const options = [
          { label: t('common.no'), value: false },
          { label: t('common.yes'), value: true },
        ]
        const selectedLabel =
          data.previouslyMarried === true
            ? t('common.yes')
            : data.previouslyMarried === false
              ? t('common.no')
              : ''

        return (
          <div className="space-y-12">
            <h3 className="text-3xl sm:text-4xl font-normal text-[#2D2A35] text-center">
              {isGuardianIntake(data)
                ? t('intake.family.q_prevMarried_guardian')
                : t('intake.family.q_prevMarried_candidate')}
            </h3>

            <RadioGroup
              options={options.map((o) => o.label)}
              value={selectedLabel}
              onChange={(label) => {
                const val = options.find((o) => o.label === label)?.value
                update({
                  previouslyMarried: typeof val === 'boolean' ? val : null,
                  hasChildren: null,
                  childrenLivingWith: '',
                  openToMoreChildren: '',
                })
              }}
            />
          </div>
        )
      },
      isValid: (data) => data.previouslyMarried !== null,
    },

    {
      section: t('intake.sections.family'),
      shouldShow: (data) => data.previouslyMarried === true && data.intakeRole !== 'guardian',
      render: (data, update) => {
        const selectedLabel =
          data.hasChildren === true
            ? t('common.yes')
            : data.hasChildren === false
              ? t('common.no')
              : ''

        return (
          <div className="space-y-12">
            <h3 className="text-3xl sm:text-4xl font-normal text-[#2D2A35] text-center">
              {t('intake.family.q_hasChildren')}
            </h3>

            <RadioGroup
              options={[t('common.no'), t('common.yes')]}
              value={selectedLabel}
              onChange={(label) =>
                update({
                  hasChildren: label === t('common.yes'),
                  childrenLivingWith: '',
                  openToMoreChildren: '',
                  fam_children_count: '',
                })
              }
            />
          </div>
        )
      },
      isValid: (data) => data.previouslyMarried !== true || data.hasChildren !== null,
    },

    {
      section: t('intake.sections.family'),
      shouldShow: (data) =>
        data.previouslyMarried === true &&
        data.hasChildren === true &&
        data.intakeRole !== 'guardian',
      render: (data, update) => {
        const options = [
          { label: t('common.yes'), value: 'yes' as const },
          { label: t('common.no'), value: 'no' as const },
          { label: t('intake.family.someOfTheTime'), value: 'some' as const },
        ]
        const selectedLabel =
          options.find((o) => o.value === data.childrenLivingWith)?.label ?? ''

        return (
          <div className="space-y-12">
            <h3 className="text-3xl sm:text-4xl font-normal text-[#2D2A35] text-center">
              {t('intake.family.q_childrenLivingWith')}
            </h3>
            <RadioGroup
              options={options.map((o) => o.label)}
              value={selectedLabel}
              onChange={(label) => {
                const selected = options.find((o) => o.label === label)?.value ?? ''
                update({ childrenLivingWith: selected })
              }}
            />
          </div>
        )
      },
      isValid: (data) =>
        !(data.previouslyMarried === true && data.hasChildren === true) ||
        !!data.childrenLivingWith,
    },

    {
      section: t('intake.sections.family'),
      shouldShow: (data) =>
        data.previouslyMarried === true &&
        data.hasChildren === true &&
        data.intakeRole !== 'guardian',
      render: (data, update) => {
        const options = [
          { label: t('common.yes'), value: 'yes' as const },
          { label: t('common.no'), value: 'no' as const },
          { label: t('intake.family.unsure'), value: 'unsure' as const },
        ]
        const selectedLabel =
          options.find((o) => o.value === data.openToMoreChildren)?.label ?? ''

        return (
          <div className="space-y-12">
            <h3 className="text-3xl sm:text-4xl font-normal text-[#2D2A35] text-center">
              {t('intake.family.q_openMoreChildren')}
            </h3>
            <RadioGroup
              options={options.map((o) => o.label)}
              value={selectedLabel}
              onChange={(label) => {
                const selected = options.find((o) => o.label === label)?.value ?? ''
                update({ openToMoreChildren: selected })
              }}
            />
          </div>
        )
      },
      isValid: (data) =>
        !(data.previouslyMarried === true && data.hasChildren === true) ||
        !!data.openToMoreChildren,
    },

    {
      section: t('intake.sections.family'),
      shouldShow: (data) =>
        data.intakeRole !== 'guardian' &&
        (data.previouslyMarried === false ||
          (data.previouslyMarried === true && data.hasChildren === false)),
      render: (data, update) => (
        <div className="space-y-12">
          <h3 className="text-3xl sm:text-4xl font-normal text-[#2D2A35] text-center">
            {t('intake.family.q_childrenCount')}
          </h3>
          <RadioGroup
            options={['0', '1', '2', '3', '4', '5', '6+']}
            value={data.fam_children_count}
            onChange={(val) => update({ fam_children_count: val })}
          />
        </div>
      ),
      isValid: (data) =>
        !(
          data.intakeRole !== 'guardian' &&
          (data.previouslyMarried === false ||
            (data.previouslyMarried === true && data.hasChildren === false))
        ) || !!data.fam_children_count,
    },

    {
      section: t('intake.sections.family'),
      shouldShow: (data) => data.intakeRole !== 'guardian',
      render: (data, update) => (
        <ScaleQuestion
          question={t('intake.family.q_finances')}
          minLabel={`${t('common.strongly')}|${t('common.disagree')}`}
          maxLabel={`${t('common.strongly')}|${t('common.agree')}`}
          value={data.fam_finances}
          onChange={(val) => update({ fam_finances: val })}
        />
      ),
      isValid: (data) => data.fam_finances !== null,
    },

    {
      section: t('intake.sections.family'),
      shouldShow: (data) => data.intakeRole !== 'guardian',
      render: (data, update) => (
        <ScaleQuestion
          question={t('intake.family.q_inlaws')}
          minLabel={`${t('common.strongly')}|${t('common.disagree')}`}
          maxLabel={`${t('common.strongly')}|${t('common.agree')}`}
          value={data.fam_inlaws}
          onChange={(val) => update({ fam_inlaws: val })}
        />
      ),
      isValid: (data) => data.fam_inlaws !== null,
    },

    {
      section: t('intake.sections.family'),
      shouldShow: (data) => data.intakeRole !== 'guardian',
      render: (data, update) => (
        <ScaleQuestion
          question={t('intake.family.q_parentsDecisions')}
          minLabel={`${t('common.strongly')}|${t('common.disagree')}`}
          maxLabel={`${t('common.strongly')}|${t('common.agree')}`}
          value={data.fam_parents_decisions}
          onChange={(val) => update({ fam_parents_decisions: val })}
        />
      ),
      isValid: (data) => data.fam_parents_decisions !== null,
    },

    // -------------------------
    // FAITH & VALUES
    // -------------------------
    {
      section: t('intake.sections.faith'),
      shouldShow: (data) => data.intakeRole !== 'guardian',
      render: (data, update) => (
        <ScaleQuestion
          question={t('intake.faith.q_role')}
          minLabel={`${t('common.strongly')}|${t('common.disagree')}`}
          maxLabel={`${t('common.strongly')}|${t('common.agree')}`}
          value={data.faith_role}
          onChange={(val) => update({ faith_role: val })}
        />
      ),
      isValid: (data) => data.faith_role !== null,
    },

    {
      section: t('intake.sections.faith'),
      shouldShow: (data) => data.intakeRole !== 'guardian',
      render: (data, update) => (
        <ScaleQuestion
          question={t('intake.faith.q_denomImportance')}
          minLabel={`${t('common.strongly')}|${t('common.disagree')}`}
          maxLabel={`${t('common.strongly')}|${t('common.agree')}`}
          value={data.faith_denomination_importance}
          onChange={(val) =>
            update({
              faith_denomination_importance: val,
              ...(val !== null && val < 6
                ? { faith_denomination: '', faith_denomination_other: '' }
                : {}),
            })
          }
        />
      ),
      isValid: (data) => data.faith_denomination_importance !== null,
    },

    {
      section: t('intake.sections.faith'),
      shouldShow: (data) =>
        data.intakeRole !== 'guardian' && (data.faith_denomination_importance ?? 0) >= 6,
      render: (data, update) => {
        const options = [
          { label: t('intake.faith.denom.none'), value: 'none' as const },
          { label: t('intake.faith.denom.sunni'), value: 'sunni' as const },
          { label: t('intake.faith.denom.shia'), value: 'shia' as const },
          { label: t('intake.faith.denom.sufi'), value: 'sufi' as const },
          { label: t('intake.faith.denom.salafi'), value: 'salafi' as const },
          { label: t('intake.faith.denom.preferNot'), value: 'prefer_not' as const },
          { label: t('intake.faith.denom.other'), value: 'other' as const },
        ]
        const selectedLabel =
          options.find((o) => o.value === data.faith_denomination)?.label ?? ''

        return (
          <div className="space-y-12 max-w-xl mx-auto">
            <h3 className="text-3xl sm:text-4xl font-normal text-[#2D2A35] text-center">
              {t('intake.faith.q_denomWhich')}
            </h3>

            <RadioGroup
              options={options.map((o) => o.label)}
              value={selectedLabel}
              onChange={(label) => {
                const selected = options.find((o) => o.label === label)?.value ?? ''
                update({
                  faith_denomination: selected as any,
                  faith_denomination_other:
                    selected === 'other' ? data.faith_denomination_other : '',
                })
              }}
            />

            {data.faith_denomination === 'other' && (
              <TextInput
                label={t('intake.faith.denom.otherLabel')}
                value={data.faith_denomination_other}
                onChange={(e) => update({ faith_denomination_other: e.target.value })}
                placeholder={t('intake.faith.denom.otherPlaceholder')}
              />
            )}
          </div>
        )
      },
      isValid: () => true,
    },

    {
      section: t('intake.sections.faith'),
      shouldShow: (data) => data.intakeRole !== 'guardian',
      render: (data, update) => (
        <ScaleQuestion
          question={t('intake.faith.q_halalFood')}
          minLabel={`${t('common.strongly')}|${t('common.disagree')}`}
          maxLabel={`${t('common.strongly')}|${t('common.agree')}`}
          value={data.faith_halal_food}
          onChange={(val) => update({ faith_halal_food: val })}
        />
      ),
      isValid: (data) => data.faith_halal_food !== null,
    },

    {
      section: t('intake.sections.faith'),
      shouldShow: (data) => data.intakeRole !== 'guardian',
      render: (data, update) => (
        <ScaleQuestion
          question={t('intake.faith.q_ramadan')}
          minLabel={`${t('common.strongly')}|${t('common.disagree')}`}
          maxLabel={`${t('common.strongly')}|${t('common.agree')}`}
          value={data.faith_ramadan}
          onChange={(val) => update({ faith_ramadan: val })}
        />
      ),
      isValid: (data) => data.faith_ramadan !== null,
    },

    {
      section: t('intake.sections.faith'),
      shouldShow: (data) => data.intakeRole !== 'guardian',
      render: (data, update) => (
        <ScaleQuestion
          question={t('intake.faith.q_halalIncome')}
          minLabel={`${t('common.strongly')}|${t('common.disagree')}`}
          maxLabel={`${t('common.strongly')}|${t('common.agree')}`}
          value={data.faith_halal_income}
          onChange={(val) => update({ faith_halal_income: val })}
        />
      ),
      isValid: (data) => data.faith_halal_income !== null,
    },

    {
      section: t('intake.sections.faith'),
      shouldShow: (data) => data.intakeRole !== 'guardian' && data.fam_children_count !== '0',
      render: (data, update) => (
        <ScaleQuestion
          question={t('intake.faith.q_childrenReligious')}
          minLabel={`${t('common.strongly')}|${t('common.disagree')}`}
          maxLabel={`${t('common.strongly')}|${t('common.agree')}`}
          value={data.faith_children}
          onChange={(val) => update({ faith_children: val })}
        />
      ),
      isValid: (data) => data.faith_children !== null,
    },

    // -------------------------
    // OPENNESS & LOGISTICS  (you already converted these — kept consistent)
    // -------------------------
    {
      section: t('intake.sections.openness'),
      render: (data, update) => (
        <ScaleQuestion
          question={t('intake.openness.abroad')}
          minLabel={`${t('common.strongly')}|${t('common.disagree')}`}
          maxLabel={`${t('common.strongly')}|${t('common.agree')}`}
          value={data.open_children_abroad}
          onChange={(val) => update({ open_children_abroad: val })}
        />
      ),
      isValid: (data) => data.open_children_abroad !== null,
      shouldShow: (data) => data.intakeRole !== 'guardian' && data.fam_children_count !== '0',
    },
    {
      section: t('intake.sections.openness'),
      render: (data, update) => (
        <ScaleQuestion
          question={t('intake.openness.childrenLang')}
          minLabel={`${t('common.strongly')}|${t('common.disagree')}`}
          maxLabel={`${t('common.strongly')}|${t('common.agree')}`}
          value={data.open_children_lang}
          onChange={(val) => update({ open_children_lang: val })}
        />
      ),
      isValid: (data) => data.open_children_lang !== null,
      shouldShow: (data) => data.intakeRole !== 'guardian' && data.fam_children_count !== '0',
    },
    {
      section: t('intake.sections.openness'),
      shouldShow: (data) => data.intakeRole !== 'guardian',
      render: (data, update) => (
        <ScaleQuestion
          question={t('intake.openness.longDistance')}
          minLabel={`${t('common.strongly')}|${t('common.disagree')}`}
          maxLabel={`${t('common.strongly')}|${t('common.agree')}`}
          value={data.open_long_distance}
          onChange={(val) => update({ open_long_distance: val })}
        />
      ),
      isValid: (data) => data.open_long_distance !== null,
    },
    {
      section: t('intake.sections.openness'),
      shouldShow: (data) => data.intakeRole !== 'guardian',
      render: (data, update) => (
        <ScaleQuestion
          question={t('intake.openness.financialRisk')}
          minLabel={`${t('common.strongly')}|${t('common.disagree')}`}
          maxLabel={`${t('common.strongly')}|${t('common.agree')}`}
          value={data.open_financial_risk}
          onChange={(val) => update({ open_financial_risk: val })}
        />
      ),
      isValid: (data) => data.open_financial_risk !== null,
    },
    {
      section: t('intake.sections.openness'),
      shouldShow: (data) => data.intakeRole !== 'guardian',
      render: (data, update) => (
        <ScaleQuestion
          question={t('intake.openness.financialSupport')}
          minLabel={`${t('common.strongly')}|${t('common.disagree')}`}
          maxLabel={`${t('common.strongly')}|${t('common.agree')}`}
          value={data.open_financial_support}
          onChange={(val) => update({ open_financial_support: val })}
        />
      ),
      isValid: (data) => data.open_financial_support !== null,
    },

    // -------------------------
    // PREFERENCES
    // -------------------------
    {
      section: t('intake.sections.preferences'),
      render: (data, update) => (
        <MultiSelect
          label={
            isGuardianIntake(data)
              ? t('intake.preferences.agePreferred')
              : t('intake.preferences.ageOpenTo')
          }
          options={['18–24', '25–30', '31–35', '36–41', '42–46', '47+']}
          selected={data.age_preferences}
          onChange={(selected) => update({ age_preferences: selected })}
        />
      ),
      isValid: (data) => data.age_preferences.length > 0,
      helperText: t('common.selectAll'),
    },

    // -------------------------
    // INTRODUCTIONS (candidate-only)
    // NOTE: stores stable codes in guardianIntroRoles: ['parent','wali','sibling','friend','other']
    // -------------------------
    {
      section: t('intake.sections.introductions'),
      shouldShow: (data) => data.intakeRole !== 'guardian',
      render: (data, update) => {
        const selectedLabel =
          data.allowGuardianLedIntros === true
            ? t('common.yes')
            : data.allowGuardianLedIntros === false
              ? t('common.no')
              : ''

        return (
          <div className="space-y-12">
            <h3 className="text-3xl sm:text-4xl font-normal text-[#2D2A35] text-center">
              {t('intake.introductions.q1')}
            </h3>

            <RadioGroup
              options={[t('common.no'), t('common.yes')]}
              value={selectedLabel}
              onChange={(label) =>
                update({
                  allowGuardianLedIntros: label === t('common.yes'),
                  guardianIntroRoles: [],
                })
              }
            />
          </div>
        )
      },
      isValid: (data) => data.allowGuardianLedIntros !== null,
    },

    {
      section: t('intake.sections.introductions'),
      shouldShow: (data) => data.intakeRole !== 'guardian' && data.allowGuardianLedIntros === true,
      render: (data, update) => {
        const roleOptions = [
          { code: 'parent', label: t('intake.roles.parent') },
          { code: 'wali', label: t('intake.roles.wali') },
          { code: 'sibling', label: t('intake.roles.sibling') },
          { code: 'friend', label: t('intake.roles.friend') },
          { code: 'other', label: t('intake.roles.other') },
        ]

        // UI shows translated labels
        const selectedLabels = roleOptions
          .filter((o) => data.guardianIntroRoles.includes(o.code))
          .map((o) => o.label)

        return (
          <div className="space-y-12">
            <MultiSelect
              label={t('intake.introductions.q2')}
              options={roleOptions.map((o) => o.label)}
              selected={selectedLabels}
              onChange={(labels) => {
                const codes = roleOptions
                  .filter((o) => labels.includes(o.label))
                  .map((o) => o.code)
                update({ guardianIntroRoles: codes })
              }}
            />
          </div>
        )
      },
      isValid: (data) => (data.guardianIntroRoles?.length ?? 0) > 0,
      helperText: t('common.selectAll'),
    },

    // -------------------------
    // SPOUSE PREFERENCES (guardian-only)
    // -------------------------
    {
      section: t('intake.sections.spousePreferences'),
      shouldShow: (data) => data.intakeRole === 'guardian',
      render: () => (
        <div className="space-y-10 max-w-2xl mx-auto text-center">
          <h3 className="text-3xl sm:text-4xl font-normal text-[#2D2A35]">
            {t('intake.spouse.introTitle')}
          </h3>

          <p className="text-lg text-[#6B6676] leading-relaxed">
            {t('intake.spouse.introBody')}
          </p>
        </div>
      ),
      isValid: () => true,
    },

    {
      section: t('intake.sections.spousePreferences'),
      shouldShow: (data) => data.intakeRole === 'guardian',
      render: (data, update) => (
        <ScaleQuestion
          question={t('intake.spouse.q_faith_role')}
          minLabel={t('common.notImportant')}
          maxLabel={t('common.veryImportant')}
          value={data.pref_faith_role}
          onChange={(val) => update({ pref_faith_role: val })}
        />
      ),
      isValid: (data) => data.pref_faith_role !== null,
    },
    {
      section: t('intake.sections.spousePreferences'),
      shouldShow: (data) => data.intakeRole === 'guardian',
      render: (data, update) => (
        <ScaleQuestion
          question={t('intake.spouse.q_faith_ramadan')}
          minLabel={t('common.notImportant')}
          maxLabel={t('common.veryImportant')}
          value={data.pref_faith_ramadan}
          onChange={(val) => update({ pref_faith_ramadan: val })}
        />
      ),
      isValid: (data) => data.pref_faith_ramadan !== null,
    },
    {
      section: t('intake.sections.spousePreferences'),
      shouldShow: (data) => data.intakeRole === 'guardian',
      render: (data, update) => (
        <ScaleQuestion
          question={t('intake.spouse.q_faith_halal_food')}
          minLabel={t('common.notImportant')}
          maxLabel={t('common.veryImportant')}
          value={data.pref_faith_halal_food}
          onChange={(val) => update({ pref_faith_halal_food: val })}
        />
      ),
      isValid: (data) => data.pref_faith_halal_food !== null,
    },
    {
      section: t('intake.sections.spousePreferences'),
      shouldShow: (data) => data.intakeRole === 'guardian',
      render: (data, update) => (
        <ScaleQuestion
          question={t('intake.spouse.q_faith_halal_income')}
          minLabel={t('common.notImportant')}
          maxLabel={t('common.veryImportant')}
          value={data.pref_faith_halal_income}
          onChange={(val) => update({ pref_faith_halal_income: val })}
        />
      ),
      isValid: (data) => data.pref_faith_halal_income !== null,
    },
    {
      section: t('intake.sections.spousePreferences'),
      shouldShow: (data) => data.intakeRole === 'guardian',
      render: (data, update) => (
        <ScaleQuestion
          question={t('intake.spouse.q_fam_inlaws')}
          minLabel={t('common.notImportant')}
          maxLabel={t('common.veryImportant')}
          value={data.pref_fam_inlaws}
          onChange={(val) => update({ pref_fam_inlaws: val })}
        />
      ),
      isValid: (data) => data.pref_fam_inlaws !== null,
    },
    {
      section: t('intake.sections.spousePreferences'),
      shouldShow: (data) => data.intakeRole === 'guardian',
      render: (data, update) => (
        <ScaleQuestion
          question={t('intake.spouse.q_fam_parents')}
          minLabel={t('common.notImportant')}
          maxLabel={t('common.veryImportant')}
          value={data.pref_fam_parents_decisions}
          onChange={(val) => update({ pref_fam_parents_decisions: val })}
        />
      ),
      isValid: (data) => data.pref_fam_parents_decisions !== null,
    },
    {
      section: t('intake.sections.spousePreferences'),
      shouldShow: (data) => data.intakeRole === 'guardian',
      render: (data, update) => (
        <ScaleQuestion
          question={t('intake.spouse.q_fam_finances')}
          minLabel={t('common.notImportant')}
          maxLabel={t('common.veryImportant')}
          value={data.pref_fam_finances}
          onChange={(val) => update({ pref_fam_finances: val })}
        />
      ),
      isValid: (data) => data.pref_fam_finances !== null,
    },
    {
      section: t('intake.sections.spousePreferences'),
      shouldShow: (data) => data.intakeRole === 'guardian',
      render: (data, update) => (
        <ScaleQuestion
          question={t('intake.spouse.q_rel_conflict')}
          minLabel={t('common.notImportant')}
          maxLabel={t('common.veryImportant')}
          value={data.pref_rel_conflict}
          onChange={(val) => update({ pref_rel_conflict: val })}
        />
      ),
      isValid: (data) => data.pref_rel_conflict !== null,
    },
    {
      section: t('intake.sections.spousePreferences'),
      shouldShow: (data) => data.intakeRole === 'guardian',
      render: (data, update) => (
        <ScaleQuestion
          question={t('intake.spouse.q_rel_openness')}
          minLabel={t('common.notImportant')}
          maxLabel={t('common.veryImportant')}
          value={data.pref_rel_openness}
          onChange={(val) => update({ pref_rel_openness: val })}
        />
      ),
      isValid: (data) => data.pref_rel_openness !== null,
    },
    {
      section: t('intake.sections.spousePreferences'),
      shouldShow: (data) => data.intakeRole === 'guardian',
      render: (data, update) => (
        <ScaleQuestion
          question={t('intake.spouse.q_rel_processing')}
          minLabel={t('common.notImportant')}
          maxLabel={t('common.veryImportant')}
          value={data.pref_rel_processing}
          onChange={(val) => update({ pref_rel_processing: val })}
        />
      ),
      isValid: (data) => data.pref_rel_processing !== null,
    },
    {
      section: t('intake.sections.spousePreferences'),
      shouldShow: (data) => data.intakeRole === 'guardian',
      render: (data, update) => (
        <ScaleQuestion
          question={t('intake.spouse.q_lifestyle_travel')}
          minLabel={t('common.notImportant')}
          maxLabel={t('common.veryImportant')}
          value={data.pref_lifestyle_travel}
          onChange={(val) => update({ pref_lifestyle_travel: val })}
        />
      ),
      isValid: (data) => data.pref_lifestyle_travel !== null,
    },
    {
      section: t('intake.sections.spousePreferences'),
      shouldShow: (data) => data.intakeRole === 'guardian',
      render: (data, update) => (
        <ScaleQuestion
          question={t('intake.spouse.q_lifestyle_exercise')}
          minLabel={t('common.notImportant')}
          maxLabel={t('common.veryImportant')}
          value={data.pref_lifestyle_exercise}
          onChange={(val) => update({ pref_lifestyle_exercise: val })}
        />
      ),
      isValid: (data) => data.pref_lifestyle_exercise !== null,
    },

    // -------------------------
    // CONTACT
    // -------------------------
    {
      section: t('intake.sections.contact'),
      render: (data, update) => {
        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/
        const isEmailValid = emailRegex.test(data.email)
        const showEmailError = data.email.trim().length > 0 && !isEmailValid

        return (
          <div className="space-y-12 max-w-xl mx-auto">
            <h3 className="text-3xl sm:text-4xl font-normal text-[#2D2A35] leading-tight tracking-tight text-center">
              {t('intake.contact.title')}
            </h3>

            <div className="space-y-8">
              <TextInput
                label={t('intake.contact.email')}
                type="email"
                value={data.email}
                onChange={(e) => update({ email: e.target.value })}
                placeholder={t('intake.placeholders.email')}
                error={showEmailError ? t('intake.errors.emailInvalid') : undefined}
                autoFocus
              />

              <TextInput
                label={t('intake.contact.phone')}
                type="tel"
                value={data.phone}
                onChange={(e) => update({ phone: e.target.value.trim() })}
                placeholder={t('intake.placeholders.phone')}
              />
            </div>
          </div>
        )
      },
      isValid: (data) => {
        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/
        return data.email.trim().length > 0 && emailRegex.test(data.email.trim())
      },
      helperText: t('intake.contact.helper'),
    },

    // -------------------------
    // PHOTO (optional — before reflection)
    // -------------------------
    {
      section: t('intake.sections.photo'),
      render: (data, update) => {
        const handleFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
          const file = e.target.files?.[0]
          if (!file) return

          // 1) local preview (optional, keep your current UX)
          const reader = new FileReader()
          reader.onloadend = () => {
            update({
              photoFile: file,
              photoPreview: reader.result as string,
            })
          }
          reader.readAsDataURL(file)

          // 2) get signed upload target from Edge Function
          // (new function URL env var; see section D)
          const createUploadRes = await fetch(import.meta.env.VITE_CREATE_PHOTO_UPLOAD_URL, {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              Authorization: `Bearer ${import.meta.env.VITE_SUPABASE_ANON_KEY}`,
              apikey: import.meta.env.VITE_SUPABASE_ANON_KEY,
            },
            body: JSON.stringify({
              content_type: file.type || 'image/jpeg',
              file_name: file.name,
            }),
          })

          if (!createUploadRes.ok) {
            console.error('create-photo-upload failed:', await createUploadRes.text())
            // keep preview but do not block step completion
            return
          }

          const { upload_url, photo_path } = await createUploadRes.json()

          // 3) upload file to signed URL (PUT)
          const putRes = await fetch(upload_url, {
            method: 'PUT',
            headers: {
              'Content-Type': file.type || 'application/octet-stream',
              // sometimes required by storage signed urls:
              // 'x-upsert': 'true',
            },
            body: file,
          })

          if (!putRes.ok) {
            console.error('photo upload PUT failed:', await putRes.text())
            return
          }

          // 4) store only the stable reference for submission
          update({ photo_path })
        }

        const handleRemove = () => {
          update({
            photoFile: null,
            photoPreview: '',
            photo_path: null,
          })
        }

        return (
          <div className="space-y-10 max-w-lg mx-auto text-center">
            <div className="space-y-4">
              <h3 className="text-3xl sm:text-4xl font-normal text-[#2D2A35] leading-tight tracking-tight">
                {t('intake.photo.title')}
              </h3>
              <p className="text-lg text-[#57534E] font-light leading-relaxed">
                {t('intake.photo.descriptionBeforeEmphasis')}{' '}
                <span className="text-[#9B87C7] font-medium">
                  {t('intake.photo.descriptionEmphasis')}
                </span>{' '}
                {t('intake.photo.descriptionAfterEmphasis')}
              </p>
            </div>

            <div className="flex justify-center">
              {data.photoPreview ? (
                <div className="relative group">
                  <div className="w-44 h-44 sm:w-52 sm:h-52 rounded-full overflow-hidden organic-shadow ring-4 ring-[#E8E4EE]/60">
                    <img
                      src={data.photoPreview}
                      alt={t('intake.photo.previewAlt')}
                      className="w-full h-full object-cover"
                    />
                  </div>
                  <button
                    type="button"
                    onClick={handleRemove}
                    className="absolute -bottom-2 left-1/2 -translate-x-1/2 bg-white border border-[#E8E4EE] rounded-full px-5 py-2 text-sm text-[#9B95A3] hover:text-[#2D2A35] hover:border-[#9B87C7] transition-all duration-300 shadow-sm"
                  >
                    {t('intake.photo.remove')}
                  </button>
                </div>
              ) : (
                <label className="cursor-pointer group">
                  <input
                    type="file"
                    accept="image/*"
                    onChange={handleFileChange}
                    className="hidden"
                  />
                  <div className="w-44 h-44 sm:w-52 sm:h-52 rounded-full border-2 border-dashed border-[#D4CDD9] group-hover:border-[#9B87C7] transition-all duration-500 flex flex-col items-center justify-center gap-4 bg-[#FDFCFE] group-hover:bg-white group-hover:shadow-lg">
                    <div className="w-14 h-14 rounded-full bg-gradient-to-br from-[#9B87C7]/15 to-[#B5A8D4]/10 flex items-center justify-center group-hover:from-[#9B87C7]/25 group-hover:to-[#B5A8D4]/20 transition-all duration-500">
                      <svg
                        width="24"
                        height="24"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="#9B87C7"
                        strokeWidth="1.5"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        className="group-hover:scale-110 transition-transform duration-300"
                      >
                        <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2" />
                        <circle cx="12" cy="7" r="4" />
                      </svg>
                    </div>
                    <span className="text-sm text-[#9B95A3] group-hover:text-[#9B87C7] font-light tracking-wide transition-colors duration-300">
                      {t('intake.photo.tapToUpload')}
                    </span>
                  </div>
                </label>
              )}
            </div>

            <div className="flex items-center justify-center gap-2 pt-2">
              <svg
                width="14"
                height="14"
                viewBox="0 0 24 24"
                fill="none"
                stroke="#B5A8D4"
                strokeWidth="1.5"
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <rect x="3" y="11" width="18" height="11" rx="2" ry="2" />
                <path d="M7 11V7a5 5 0 0 1 10 0v4" />
              </svg>
              <p className="text-xs text-[#B5A8C3] font-light">
                {t('intake.photo.privacyNote')}
              </p>
            </div>
          </div>
        )
      },
      isValid: () => true,
      helperText: t('intake.photo.helper'),
    },

    // -------------------------
    // FINAL REFLECTION
    // -------------------------
    {
      section: t('intake.sections.finalReflection'),
      render: (data, update) => (
        <div className="space-y-8 max-w-2xl mx-auto">
          <label className="block text-3xl sm:text-4xl font-normal text-[#2D2A35] leading-tight text-center">
            {isGuardianIntake(data)
              ? t('intake.reflection.labelGuardian')
              : t('intake.reflection.labelCandidate')}
          </label>

          <textarea
            value={data.reflection}
            onChange={(e) => update({ reflection: e.target.value })}
            className="w-full h-56 p-8 rounded-3xl border border-[#E8E4EE] bg-white text-[#2D2A35] placeholder-[#9B95A3]/50 focus:outline-none focus:border-[#9B87C7] resize-none transition-all duration-500 text-xl leading-relaxed shadow-sm"
            placeholder={t('intake.reflection.placeholder')}
            autoFocus
          />
        </div>
      ),
      isValid: () => true,
      helperText: t('intake.reflection.helper'),
    },
  ]
  // Filter questions based on conditional logic
  const visibleQuestions = useMemo(
    () => questions.filter((q) => !q.shouldShow || q.shouldShow(formData)),
    [questions, formData],
  )
  const totalSteps = visibleQuestions.length
// Handle URL-based step jumps (success only)
useEffect(() => {
  const step = searchParams.get('step') // "success" | null

  // If anything links to ?step=success, route to final confirmation page
  if (step === 'success') {
    navigate('/checkout/success' + window.location.search, { replace: true })
    return
  }

  // If old links still use ?step=payment, just send them to success now
  if (step === 'payment') {
    navigate('/checkout/success' + window.location.search, { replace: true })
    return
  }
}, [searchParams, totalSteps, navigate])

// ✅ Pricing step removed — no redirect effect needed anymore
// (intentionally deleted)

const nextStep = async () => {
  // If we are on completion screen, go straight to final confirmation page
  if (currentIndex === totalSteps + 1) {
    navigate('/checkout/success' + window.location.search, { replace: true })
    return
  }

  // If we are on the LAST question screen, submit BEFORE moving to completion
  if (currentIndex === totalSteps) {
    try {
      const payload = buildIntakePayload(formData)
      console.log('Submitting payload:', payload)

      const res = await fetch(import.meta.env.VITE_INTAKE_SUBMIT_URL, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${import.meta.env.VITE_SUPABASE_ANON_KEY}`,
          apikey: import.meta.env.VITE_SUPABASE_ANON_KEY,
        },
        body: JSON.stringify(payload),
      })

      const text = await res.text()
      console.log('submit-intake status:', res.status)
      console.log('submit-intake response:', text)

      if (!res.ok) throw new Error(text || `HTTP ${res.status}`)

      try {
        localStorage.setItem('niyyah_intake_complete', '1')
      } catch {}
    } catch (err) {
      console.error('Intake submit failed:', err)
      return
    }
  }

  setDirection(1)
  setCurrentIndex((prev) => prev + 1)
  window.scrollTo({
    top: 0,
    behavior: 'smooth',
  })
}

const prevStep = () => {
  setDirection(-1)
  setCurrentIndex((prev) => prev - 1)
  window.scrollTo({
    top: 0,
    behavior: 'smooth',
  })
}

const variants = {
  enter: (direction: number) => ({
    x: direction > 0 ? 30 : -30,
    opacity: 0,
  }),
  center: {
    x: 0,
    opacity: 1,
  },
  exit: (direction: number) => ({
    x: direction > 0 ? -30 : 30,
    opacity: 0,
  }),
}
  // Calculate progress for background circle
  const progress =
    currentIndex > 0 && currentIndex <= totalSteps
      ? (currentIndex / totalSteps) * 100
      : 0
  // Render welcome screen
  if (currentIndex === 0) {
    return (
      <div className="min-h-screen bg-[#FDFCFE] text-[#2D2A35] font-sans selection:bg-[#B5A8D4]/20 overflow-hidden">
        <div className="max-w-2xl mx-auto px-6 py-12 sm:py-20 relative z-10">
          <motion.div
            initial={{
              opacity: 0,
              y: -20,
            }}
            animate={{
              opacity: 1,
              y: 0,
            }}
            transition={{
              duration: 1,
              ease: [0.25, 0.1, 0.25, 1],
            }}
            className="mb-16 flex justify-center relative"
          >
            <span className="font-semibold text-xl tracking-tight niyyah-gradient">
              Niyyah
            </span>

            {/* Language switcher — globe that unfolds */}
            <div className="absolute right-0 top-1/2 -translate-y-1/2 flex items-center">
              <AnimatePresence mode="wait">
                {!langOpen ? (
                  <motion.button
                    key="globe"
                    initial={{ opacity: 0, scale: 0.8 }}
                    animate={{ opacity: 1, scale: 1 }}
                    exit={{ opacity: 0, scale: 0.8 }}
                    transition={{ duration: 0.3, ease: [0.25, 0.1, 0.25, 1] }}
                    onClick={() => setLangOpen(true)}
                    className="w-8 h-8 rounded-full flex items-center justify-center text-[#B5A8C3] hover:text-[#9B87C7] transition-colors duration-300"
                    aria-label="Change language"
                  >
                    <svg
                      width="18"
                      height="18"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="1.5"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    >
                      <circle cx="12" cy="12" r="10" />
                      <path d="M2 12h20" />
                      <path d="M12 2a15.3 15.3 0 0 1 4 10 15.3 15.3 0 0 1-4 10 15.3 15.3 0 0 1-4-10 15.3 15.3 0 0 1 4-10z" />
                    </svg>
                  </motion.button>
                ) : (
                  <>
                    {/* Desktop: inline unfold */}
                    <motion.div
                      key="options-desktop"
                      initial={{ opacity: 0, width: 0 }}
                      animate={{ opacity: 1, width: 'auto' }}
                      exit={{ opacity: 0, width: 0 }}
                      transition={{ duration: 0.4, ease: [0.25, 0.1, 0.25, 1] }}
                      className="hidden sm:flex items-center gap-1 overflow-hidden"
                    >
                      {(
                        [
                          { code: 'en', label: 'EN' },
                          { code: 'fr', label: 'FR' },
                          { code: 'ar', label: 'AR' },
                        ] as const
                      ).map((l, i) => (
                        <Fragment key={l.code}>
                          {i > 0 && (
                            <motion.span
                              initial={{ opacity: 0 }}
                              animate={{ opacity: 1 }}
                              transition={{ delay: 0.1 + i * 0.05 }}
                              className="text-[#D4CDD9] text-xs select-none"
                            >
                              ·
                            </motion.span>
                          )}
                          <motion.button
                            initial={{ opacity: 0, y: 4 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ delay: 0.08 + i * 0.06, duration: 0.3 }}
                            onClick={() => handleLocaleSelect(l.code)}
                            className={`text-xs tracking-widest px-1.5 py-1 transition-all duration-300 whitespace-nowrap ${
                              lang === l.code
                                ? 'text-[#2D2A35] font-medium border-b border-[#9B87C7]'
                                : 'text-[#B5A8C3] font-light hover:text-[#9B95A3]'
                            }`}
                          >
                            {l.label}
                          </motion.button>
                        </Fragment>
                      ))}
                      <motion.button
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        transition={{ delay: 0.3 }}
                        onClick={() => setLangOpen(false)}
                        className="ml-1 text-[#D4CDD9] hover:text-[#9B95A3] transition-colors duration-300"
                        aria-label="Close language picker"
                      >
                        <svg
                          width="14"
                          height="14"
                          viewBox="0 0 24 24"
                          fill="none"
                          stroke="currentColor"
                          strokeWidth="1.5"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                        >
                          <line x1="18" y1="6" x2="6" y2="18" />
                          <line x1="6" y1="6" x2="18" y2="18" />
                        </svg>
                      </motion.button>
                    </motion.div>
                  </>
                )}
              </AnimatePresence>
            </div>
          </motion.div>

          <AnimatePresence
            mode="wait"
            onExitComplete={() => {
              if (isLocaleTransitioning && nextLocale) {
                setLocale(nextLocale)
                updateData({ locale: nextLocale })
                setLang(nextLocale) // optional
                setNextLocale(null)
                setIsLocaleTransitioning(false)
                setLangOpen(false)
              }
            }}
          >
            {!isLocaleTransitioning && (
              <motion.div
                key={`welcome-${locale}`}
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                transition={{ duration: 0.35, ease: [0.25, 0.1, 0.25, 1] }}
              >
                <WelcomeStep
                  data={formData}
                  updateData={updateData}
                  onNext={nextStep}
                />
              </motion.div>
            )}
          </AnimatePresence>

          {/* Mobile: full-screen language overlay */}
          <AnimatePresence>
            {langOpen && (
              <motion.div
                key="lang-fullscreen"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                transition={{ duration: 0.4, ease: [0.25, 0.1, 0.25, 1] }}
                className="sm:hidden fixed inset-0 z-50 bg-[#FDFCFE] flex flex-col items-center justify-center"
              >
                {/* Close button */}
                <motion.button
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  transition={{ delay: 0.2 }}
                  onClick={() => setLangOpen(false)}
                  className="absolute top-6 right-6 w-10 h-10 rounded-full flex items-center justify-center text-[#B5A8C3] hover:text-[#9B87C7] transition-colors duration-300"
                  aria-label="Close"
                >
                  <svg
                    width="20"
                    height="20"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="1.5"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  >
                    <line x1="18" y1="6" x2="6" y2="18" />
                    <line x1="6" y1="6" x2="18" y2="18" />
                  </svg>
                </motion.button>

                {/* Globe icon */}
                <motion.div
                  initial={{ scale: 0.8, opacity: 0 }}
                  animate={{ scale: 1, opacity: 1 }}
                  transition={{
                    delay: 0.1,
                    duration: 0.5,
                    ease: [0.25, 0.1, 0.25, 1],
                  }}
                  className="mb-10 text-[#B5A8D4]"
                >
                  <svg
                    width="36"
                    height="36"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="1"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  >
                    <circle cx="12" cy="12" r="10" />
                    <path d="M2 12h20" />
                    <path d="M12 2a15.3 15.3 0 0 1 4 10 15.3 15.3 0 0 1-4 10 15.3 15.3 0 0 1-4-10 15.3 15.3 0 0 1 4-10z" />
                  </svg>
                </motion.div>

                {/* Language options */}
                <div className="flex flex-col items-center gap-6">
                  {(
                    [
                      { code: 'en', label: 'English' },
                      { code: 'fr', label: 'Français' },
                      { code: 'ar', label: 'العربية' },
                    ] as const
                  ).map((l, i) => (
                    <motion.button
                      key={l.code}
                      initial={{ opacity: 0, y: 20 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{
                        delay: 0.15 + i * 0.1,
                        duration: 0.5,
                        ease: [0.25, 0.1, 0.25, 1],
                      }}
                      onClick={() => handleLocaleSelect(l.code)}
                      className={`text-2xl tracking-wide px-8 py-4 rounded-2xl transition-all duration-300 min-w-[200px] ${
                        lang === l.code
                          ? 'text-[#2D2A35] font-medium bg-white border border-[#9B87C7]/30 shadow-sm'
                          : 'text-[#9B95A3] font-light hover:text-[#6B6575] hover:bg-white/50'
                      }`}
                    >
                      {l.label}
                    </motion.button>
                  ))}
                </div>

                {/* Subtle branding */}
                <motion.span
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  transition={{ delay: 0.6, duration: 0.8 }}
                  className="absolute bottom-10 font-semibold text-xl tracking-tight niyyah-gradient opacity-50"
                >
                  Niyyah
                </motion.span>
              </motion.div>
            )}
          </AnimatePresence>
        </div>
      </div>
    )
  }

  // Render completion screen
  if (currentIndex === totalSteps + 1) {
    return (
      <div className="min-h-screen bg-[#FDFCFE] text-[#2D2A35] font-sans selection:bg-[#B5A8D4]/20 overflow-hidden">
        <div className="max-w-2xl mx-auto px-6 py-12 sm:py-20 relative z-10">
          <AnimatePresence mode="wait" custom={direction}>
            <motion.div
              key="completion"
              custom={direction}
              variants={variants}
              initial="enter"
              animate="center"
              exit="exit"
              transition={{
                duration: 1,
                ease: [0.25, 0.1, 0.25, 1],
              }}
            >
              <CompletionStep onNext={nextStep} />
            </motion.div>
          </AnimatePresence>
        </div>
      </div>
    )
  }

  // Render success screen (after payment)
  if (currentIndex === totalSteps + 3) {
    return (
      <div className="min-h-screen bg-[#FDFCFE] text-[#2D2A35] font-sans selection:bg-[#B5A8D4]/20 overflow-hidden">
        <div className="max-w-2xl mx-auto px-6 py-12 sm:py-20 relative z-10">
          <motion.div
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 1, ease: [0.25, 0.1, 0.25, 1] }}
            className="mb-16 flex justify-center"
          >
            <span className="font-semibold text-xl tracking-tight niyyah-gradient">
              Niyyah
            </span>
          </motion.div>

          <AnimatePresence mode="wait" custom={direction}>
            <motion.div
              key="success"
              custom={direction}
              variants={variants}
              initial="enter"
              animate="center"
              exit="exit"
              transition={{ duration: 1, ease: [0.25, 0.1, 0.25, 1] }}
            >
              <SuccessStep />
            </motion.div>
          </AnimatePresence>
        </div>
      </div>
    )
  }
  // Render question screens
  const questionIndex = currentIndex - 1
  const currentQuestion = visibleQuestions[questionIndex]

  // If index is out of bounds, avoid crashing (no setState during render)
  if (!currentQuestion) {
    return <div className="min-h-screen bg-[#FDFCFE]" />
  }

  // Calculate circle parameters for background progress
  const radius = 45 // percentage of viewport
  const circumference = 2 * Math.PI * radius
  const strokeDashoffset = circumference - (progress / 100) * circumference
  return (
    <div className="min-h-screen bg-[#FDFCFE] text-[#2D2A35] font-sans selection:bg-[#B5A8D4]/20 overflow-hidden relative">
      {/* Large background circular progress bar - Parallax effect */}
      <motion.svg
        className="fixed inset-0 w-full h-full pointer-events-none"
        style={{
          transform: 'rotate(-90deg)',
        }}
        initial={{
          scale: 1.1,
        }}
        animate={{
          scale: 1,
        }}
        transition={{
          duration: 2,
          ease: 'easeOut',
        }}
      >
        <defs>
          <linearGradient
            id="bgProgressGradient"
            x1="0%"
            y1="0%"
            x2="100%"
            y2="0%"
          >
            <stop offset="0%" stopColor="#9B87C7" stopOpacity="0.05" />
            <stop offset="100%" stopColor="#7B9FD4" stopOpacity="0.05" />
          </linearGradient>
        </defs>

        {/* Background circle */}
        <circle
          cx="50%"
          cy="50%"
          r={`${radius}%`}
          stroke="#E8E4EE"
          strokeWidth="0.5"
          fill="none"
          opacity="0.4"
        />

        {/* Progress circle */}
        <motion.circle
          cx="50%"
          cy="50%"
          r={`${radius}%`}
          stroke="url(#bgProgressGradient)"
          strokeWidth="1"
          fill="none"
          strokeLinecap="round"
          strokeDasharray={circumference}
          initial={{
            strokeDashoffset: circumference,
          }}
          animate={{
            strokeDashoffset,
          }}
          transition={{
            duration: 1.5,
            ease: [0.25, 0.1, 0.25, 1],
          }}
        />
      </motion.svg>

      <div className="max-w-4xl mx-auto px-6 py-12 sm:py-20 relative z-10">
        <motion.div
          initial={{
            opacity: 0,
            y: -20,
          }}
          animate={{
            opacity: 1,
            y: 0,
          }}
          transition={{
            duration: 1,
            ease: [0.25, 0.1, 0.25, 1],
          }}
          className="mb-20"
        >
          <ProgressBar currentStep={currentIndex} totalSteps={totalSteps} />
        </motion.div>

        <div className="relative min-h-[500px]">
          <AnimatePresence mode="wait" custom={direction}>
            <motion.div
              key={currentIndex}
              custom={direction}
              variants={variants}
              initial="enter"
              animate="center"
              exit="exit"
              transition={{
                duration: 0.8,
                ease: [0.25, 0.1, 0.25, 1],
              }}
              className="w-full"
            >
              <QuestionStep
                section={currentQuestion.section}
                onNext={nextStep}
                onBack={currentIndex > 1 ? prevStep : undefined}
                isValid={currentQuestion.isValid(formData)}
                helperText={currentQuestion.helperText}
              >
                {currentQuestion.render(formData, updateData)}
              </QuestionStep>
            </motion.div>
          </AnimatePresence>
        </div>
      </div>
    </div>
  )
}